"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const rows = [{
  number: "01",
  eyebrow: "ENGENHARIA",
  headline: "Soluções de Pulverização para Etanol",
  body: "Os desafios comuns incluem:\n\nAvarias em máquinas de limpeza de tanques de fermentação podem criar tempos de paragem inesperados e perturbar os horários de produção.\nConfigurações incorretas de limpeza de tanques podem desperdiçar água, reduzir o impacto de limpeza e encurtar a vida útil do equipamento.\nAs colunas de destilação requerem frequentemente limpeza anual, o que pode significar tempos de paragem programados e elevados custos de serviço.\nAs zonas de evaporadores e de separação de óleo de milho podem envolver elevado teor de fibras e sólidos, difíceis de movimentar ou eliminar.\nUma limpeza deficiente pode aumentar o risco de crescimento bacteriano em áreas do processo de etanol relacionadas com o processamento alimentar.\nA limpeza manual pode aumentar as necessidades de mão de obra, preocupações de segurança e inconsistência.\nA falta de manutenção preventiva pode aumentar os custos de reparação e reduzir o desempenho de limpeza a longo prazo.\nAs unidades têm frequentemente tempo e recursos limitados para avaliar se os sistemas de limpeza de tanques estão a funcionar de forma eficiente.",
  certs: ["ISO 9001", "Cert. Spraying"],
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-06-as-18.24.17.png?v=9zztjh9tcxh",
  alt: "Sistema de pulverização industrial de alta precisão em ambiente fabril",
  imageLeft: true
}, {
  number: "02",
  eyebrow: "QUALIDADE",
  headline: "Indústria Marítima",
  body: "Os desafios comuns incluem:\n\nCorrosão de Equipamentos de Segurança Contra Incêndios: A exposição constante à água do mar pode degradar os bicos de proteção contra incêndios, reduzindo a fiabilidade durante a resposta a emergências.\nRiscos da Limpeza Manual de Tanques: A limpeza de tanques de carga e de combustível exige frequentemente a entrada em espaços confinados, representando riscos de segurança e aumentando o tempo de rotação.\nElevado Consumo de Água e Detergentes: Os métodos de lavagem tradicionais são ineficientes e desperdiçadores, elevando os custos e aumentando o impacto ambiental.\nHorários Operacionais Apertados: Os petroleiros e navios de carga devem minimizar os tempos de paragem entre cargas — tornando a eficiência de limpeza uma prioridade máxima.",
  certs: ["Normas CE", "Qualidade Total"],
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-06-as-18.22.06.png?v=8vthbhvzpoo",
  alt: "Componentes de pulverização industrial sob inspeção técnica rigorosa",
  imageLeft: false
}, {
  number: "03",
  eyebrow: "INOVAÇÃO",
  headline: "Químicos Especializados",
  body: "Principais Desafios no Fabrico de Químicos Especializados:\n\nPureza do Produto e Contaminação Cruzada: A acumulação de resíduos em secadores por pulverização e tanques pode conduzir a contaminação entre lotes.\nElevados Requisitos de Limpeza: A limpeza manual de tanques aumenta os custos de mão de obra, o consumo de água e expõe os trabalhadores a químicos agressivos.\nEficiência da Secagem por Pulverização: Uma atomização irregular ou um tamanho de gota inadequado pode reduzir a qualidade do produto e causar perdas de rendimento.\nTempos de Paragem para Manutenção: A limpeza frequente e a substituição de bicos abrandam a produção e aumentam o custo por lote.\nMateriais Corrosivos e Perigosos: Químicas agressivas exigem componentes resistentes à corrosão e sistemas de limpeza seguros e em circuito fechado.",
  certs: ["Eficiência Energ.", "Tecnologia 4.0"],
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-06-as-18.24.17.png?v=f5fejmja4no",
  alt: "Linha de produção automatizada com sistemas de pulverização integrados",
  imageLeft: true
}];
export default function Safety() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".intro-eyebrow", {
        y: 16
      });
      gsap.set(".intro-heading", {
        y: 24
      });
      gsap.to(".intro-eyebrow", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".intro-block",
          start: "top 85%",
          once: true
        }
      });
      gsap.to(".intro-heading", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        delay: 0.1,
        scrollTrigger: {
          trigger: ".intro-block",
          start: "top 85%",
          once: true
        }
      });
      const rowEls = gsap.utils.toArray<HTMLElement>(".feature-row");
      rowEls.forEach(row => {
        const imageWrap = row.querySelector(".row-image-wrap");
        const textEls = row.querySelectorAll(".row-text > *");
        if (imageWrap) {
          gsap.set(imageWrap, {
            y: 40
          });
          gsap.to(imageWrap, {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: row,
              start: "top 80%",
              once: true
            }
          });
          const img = imageWrap.querySelector(".row-image");
          if (img) {
            gsap.to(img, {
              y: -20,
              ease: "none",
              scrollTrigger: {
                trigger: row,
                start: "top bottom",
                end: "bottom top",
                scrub: true
              }
            });
          }
        }
        if (textEls.length) {
          gsap.set(textEls, {
            y: 24
          });
          gsap.to(textEls, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            stagger: 0.08,
            scrollTrigger: {
              trigger: row,
              start: "top 80%",
              once: true
            }
          });
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".intro-eyebrow", ".intro-heading", ".row-image-wrap", ".row-text > *"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31452" id="contentshowcasegrid" className="bg-background text-foreground py-32 md:py-44 overflow-hidden">
      <div ref={container} className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-12">
        <div className="intro-block mx-auto max-w-[720px] text-center">
          <div className="intro-eyebrow inline-flex items-center gap-2 rounded-lg border px-4 py-2 border-border">
            <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Excelência Técnica
            </span>
          </div>
          <h2 className="intro-heading font-default mt-8 text-4xl md:text-6xl lg:text-7xl font-normal text-foreground" style={{
          opacity: 0,
          letterSpacing: "-0.03em",
          lineHeight: 1.02
        }}>Processamento Químico</h2>
          <p className="mt-6 text-base md:text-lg font-light text-muted-foreground" style={{
          lineHeight: 1.55
        }}>A tecnologia de pulverização pode ter um impacto substancial nos seus processos e na sua rentabilidade. Um desempenho inferior dos bicos pode originar condições inseguras, desperdício significativo ou mesmo paragens dispendiosas. Os especialistas da Spraying Systems Co. compreendem o que está em jogo. Ajudá-lo-emos a evitar os riscos e a fazer cada gota contar, com opções de bicos standard e de dois fluidos. Trabalhámos com processadores químicos de todos os tipos e dimensões, ajudando a garantir qualidade, produtividade e eficiência, ao mesmo tempo que se atingem objetivos de sustentabilidade.</p>
        </div>

        <div className="mt-24 md:mt-32 flex flex-col gap-24 md:gap-32">
          {rows.map((row, i) => <div key={row.number} data-index={i} className="feature-row grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
              <div className={`row-image-wrap ${row.imageLeft ? "lg:col-span-7 lg:order-1" : "lg:col-span-7 lg:order-2"} order-1 relative`} style={{
            opacity: 0
          }}>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-0 -z-10 bg-accent/20 blur-3xl" aria-hidden="true" />
                  <div className="relative overflow-hidden rounded-2xl border border-border bg-card">
                    <img src={row.image} alt={row.alt} loading="lazy" decoding="async" className="row-image w-full h-[360px] md:h-[460px] lg:h-[520px] object-cover" />
                  </div>
                </div>
              </div>

              <div className={`row-text ${row.imageLeft ? "lg:col-span-5 lg:order-2" : "lg:col-span-5 lg:order-1"} order-2`}>
                <div className="font-serif text-4xl md:text-5xl font-normal text-primary" style={{
              opacity: 0,
              letterSpacing: "-0.02em",
              lineHeight: 1
            }}>
                  {row.number}
                </div>
                <div className="mt-6 text-xs font-medium uppercase tracking-widest text-secondary" style={{
              opacity: 0
            }}>
                  {row.eyebrow}
                </div>
                <h3 className="font-default mt-4 text-2xl md:text-3xl lg:text-4xl font-medium text-foreground" style={{
              opacity: 0,
              letterSpacing: "-0.02em",
              lineHeight: 1.12
            }}>
                  {row.headline}
                </h3>
                <p className="mt-5 text-base md:text-lg font-light text-muted-foreground" style={{
              opacity: 0,
              lineHeight: 1.55
            }}>
                  {row.body}
                </p>
                <div className="mt-6 flex flex-wrap gap-2" style={{
              opacity: 0
            }}>
                  {row.certs.map((cert, j) => <Badge key={cert} data-index={j} variant="outline" className="rounded-lg px-3 py-1 text-[11px] font-medium uppercase tracking-wider border-border bg-muted/50 text-foreground">
                      {cert}
                    </Badge>)}
                </div>
              </div>
            </div>)}
        </div>

        <div className="mt-24 md:mt-32 flex justify-center">
          <Button asChild variant="ghost" className="group h-auto rounded-lg px-0 py-2 text-base font-normal hover:bg-transparent text-foreground">
            <Link to="/ProductsPage" className="inline-flex items-center gap-2">
              <span className="relative">
                Ver catálogo completo
                <span className="absolute left-0 -bottom-1 h-px w-0 transition-all duration-300 group-hover:w-full bg-primary" />
              </span>
              <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 text-primary" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}